import type { TFunction } from 'react-i18next';
import {
  BUSINESS_APP_ACTIONS,
  EMPTY_VALUE,
  PREVIEW_VERSION_LABEL,
} from './constants';
import type {
  BusinessApp,
  BusinessAppStatus,
  BusinessAppVersion,
  PreviewEndedContext,
} from './types';

export type BusinessAppAction =
  (typeof BUSINESS_APP_ACTIONS)[keyof typeof BUSINESS_APP_ACTIONS];

export type BusinessAppPermissions = {
  canViewBusinessApps: boolean;
  canPublishBusinessApps: boolean;
  canEditWithKiCode: boolean;
};

export type BusinessAppTableRow =
  | {
      id: string;
      kind: 'app';
      app: BusinessApp;
      version?: BusinessAppVersion;
      expanded: boolean;
    }
  | {
      id: string;
      kind: 'version';
      app: BusinessApp;
      version: BusinessAppVersion;
    };

const versionKey = (version: BusinessAppVersion) =>
  version.preview_owner
    ? `preview:${version.preview_owner.id}`
    : `release:${version.version}`;

export const versionLabel = (version?: BusinessAppVersion | null) =>
  version?.status === 'preview' ? PREVIEW_VERSION_LABEL : version?.version;

export const liveVersionOf = (app: BusinessApp) =>
  app.versions.find((version) => version.status === 'live');

// With Live deleted, the newest release stands in for it
const parkedReleaseOf = (app: BusinessApp) =>
  liveVersionOf(app)
    ? undefined
    : app.versions
        .filter((version) => version.status === 'released')
        .sort((a, b) => Number(b.version) - Number(a.version))[0];

export const liveOrParkedVersionOf = (app: BusinessApp) =>
  liveVersionOf(app) ?? parkedReleaseOf(app);

const visibleVersions = (app: BusinessApp) => {
  const parked = parkedReleaseOf(app);
  return app.versions.filter(
    (version) => version.status !== 'released' || version === parked
  );
};

export const isAppDisabled = (app: BusinessApp) =>
  app.status === 'disabled' && Boolean(liveOrParkedVersionOf(app));

const topRowVersion = (app: BusinessApp) =>
  app.current_version ?? liveOrParkedVersionOf(app) ?? visibleVersions(app)[0];

const toTime = (date?: string | null) => (date ? Date.parse(date) || 0 : 0);

const byNewest = (a: BusinessAppVersion, b: BusinessAppVersion) =>
  toTime(b.updated) - toTime(a.updated);

export const otherVersions = (app: BusinessApp) => {
  const top = topRowVersion(app);
  return visibleVersions(app)
    .filter((version) => !top || versionKey(version) !== versionKey(top))
    .sort((a, b) =>
      a.status === b.status ? byNewest(a, b) : a.status === 'preview' ? 1 : -1
    );
};

export const versionStatus = (
  app: BusinessApp,
  version?: BusinessAppVersion
): BusinessAppStatus =>
  version?.status === 'preview'
    ? 'preview'
    : isAppDisabled(app)
      ? 'disabled'
      : 'live';

export const formatDateTime = (t: TFunction, value?: string | null) =>
  value
    ? t('{{date}} at {{time}}', {
        date: new Date(value).toLocaleDateString(),
        time: new Date(value).toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        }),
      })
    : EMPTY_VALUE;

export type BusinessAppOrdering =
  | 'name'
  | '-name'
  | 'updated'
  | '-updated'
  | 'published_by'
  | '-published_by';

export const publishedByOf = (
  app: BusinessApp,
  version = topRowVersion(app)
) =>
  version?.status === 'preview'
    ? version.preview_owner
    : version?.published_by ?? app.published_by;

const SORT_COMPARATORS: Record<
  string,
  (a: BusinessApp, b: BusinessApp) => number
> = {
  name: (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  updated: (a, b) =>
    toTime(topRowVersion(a)?.updated ?? a.updated) -
    toTime(topRowVersion(b)?.updated ?? b.updated),
  published_by: (a, b) =>
    (publishedByOf(a)?.full_name ?? '')
      .toLowerCase()
      .localeCompare((publishedByOf(b)?.full_name ?? '').toLowerCase()),
};

export const sortApps = (
  apps: BusinessApp[],
  ordering: BusinessAppOrdering
) => {
  const descending = ordering.startsWith('-');
  const compare = SORT_COMPARATORS[ordering.replace('-', '')];
  return [...apps].sort((a, b) => compare(a, b) * (descending ? -1 : 1));
};

export const listedApps = (apps: BusinessApp[]) =>
  apps.filter((app) => visibleVersions(app).length);

export const buildTableRows = (
  apps: BusinessApp[],
  expandedApiNames: Set<string>
): BusinessAppTableRow[] =>
  apps.flatMap((app) => {
    const expanded = expandedApiNames.has(app.api_name);
    const appRow: BusinessAppTableRow = {
      id: app.api_name,
      kind: 'app',
      app,
      version: topRowVersion(app),
      expanded,
    };
    if (!expanded) {
      return [appRow];
    }
    return [
      appRow,
      ...otherVersions(app).map(
        (version): BusinessAppTableRow => ({
          id: `${app.api_name}:${versionKey(version)}`,
          kind: 'version',
          app,
          version,
        })
      ),
    ];
  });

export const getVersionActions = (
  app: BusinessApp,
  version: BusinessAppVersion,
  {
    canViewBusinessApps,
    canPublishBusinessApps,
    canEditWithKiCode,
  }: BusinessAppPermissions,
  currentUserId?: string,
  { includeView = true, includeKiCode = true } = {}
): BusinessAppAction[] => {
  if (!canViewBusinessApps) {
    return [];
  }
  const disabled = isAppDisabled(app);
  const isPreview = version.status === 'preview';
  const isOwnPreview =
    isPreview &&
    Boolean(currentUserId) &&
    version.preview_owner?.id === currentUserId;

  const canSwitch = !version.is_current_for_viewer && !disabled;
  const canManagePublished = !isPreview && canPublishBusinessApps;
  const canManageOwnPreview = isOwnPreview && canPublishBusinessApps;
  const toggleAction =
    disabled || version.status === 'released'
      ? BUSINESS_APP_ACTIONS.enable
      : BUSINESS_APP_ACTIONS.disable;

  return [
    includeView && BUSINESS_APP_ACTIONS.view,
    includeKiCode && canEditWithKiCode && BUSINESS_APP_ACTIONS.editWithKiCode,
    canSwitch && BUSINESS_APP_ACTIONS.useVersion,
    canManagePublished && BUSINESS_APP_ACTIONS.editConfiguration,
    canManagePublished && toggleAction,
    canManageOwnPreview && BUSINESS_APP_ACTIONS.publishPreview,
    canManageOwnPreview && BUSINESS_APP_ACTIONS.deletePreview,
  ].filter(Boolean) as BusinessAppAction[];
};

export const inUseNotice = (
  t: TFunction,
  app: BusinessApp,
  version: BusinessAppVersion | undefined,
  businessName: string,
  currentUserId?: string
) => {
  const current = app.current_version;
  if (!current) {
    return t("You're not using any version of this app.");
  }
  if (current.status !== 'preview') {
    return version?.is_current_for_viewer
      ? t('This app is enabled for everyone in {{business}}.', {
          business: businessName,
        })
      : t("You're using the published version.");
  }
  return currentUserId && current.preview_owner?.id === currentUserId
    ? app.live_version
      ? t("You're using your preview.")
      : t("You're using your preview. It hasn't been published yet.")
    : t(
        "You're using {{name}}'s preview. Others in {{business}} don't see it.",
        {
          name: current.preview_owner?.full_name?.trim().split(/\s+/)[0] ?? '',
          business: businessName,
        }
      );
};

export const disabledNotice = (t: TFunction, app: BusinessApp) =>
  isAppDisabled(app)
    ? t(
        'This app is disabled. Published version {{version}} is not running for anyone.',
        {
          version: liveOrParkedVersionOf(app)?.version,
        }
      )
    : null;

export const releaseHistory = (versions: BusinessAppVersion[]) =>
  versions
    .filter((version) => version.status !== 'preview')
    .sort((a, b) => Number(b.version) - Number(a.version));

type AssistantField = {
  key?: string;
  type?: string;
  required?: boolean;
  when?: string;
  fields?: AssistantField[];
};

type SetupAssistantValues = {
  __kizen_setup_assistant_values?: Record<string, unknown>;
};

type PluginWithUserSettings = {
  base_config?: { user_setup_assistant?: { fields?: AssistantField[] } };
  employee_config?: { user_config?: SetupAssistantValues };
};

type PluginWithAppSettings = {
  base_config?: { setup_assistant?: { fields?: AssistantField[] } };
  config?: SetupAssistantValues;
};

const requiredFields = (fields: AssistantField[] = []): AssistantField[] =>
  fields.flatMap((field) => {
    if (field.when) {
      return [];
    }
    if (field.type === 'container') {
      return requiredFields(field.fields);
    }
    return field.required && field.key ? [field] : [];
  });

const isEmptySetting = (value: unknown): boolean => {
  if (
    value === undefined ||
    value === null ||
    value === '' ||
    value === false
  ) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object' && 'value' in value) {
    return isEmptySetting((value as { value: unknown }).value);
  }
  return false;
};

const countMissing = (
  fields?: AssistantField[],
  values: Record<string, unknown> = {}
) =>
  requiredFields(fields).filter((field) =>
    isEmptySetting(values[field.key as string])
  ).length;

export const countMissingUserSettings = (plugin?: PluginWithUserSettings) =>
  countMissing(
    plugin?.base_config?.user_setup_assistant?.fields,
    plugin?.employee_config?.user_config?.__kizen_setup_assistant_values
  );

export const countMissingAppSettings = (plugin?: PluginWithAppSettings) =>
  countMissing(
    plugin?.base_config?.setup_assistant?.fields,
    plugin?.config?.__kizen_setup_assistant_values
  );

type AppInPreview = {
  apiName: string;
  name: string;
  ownerId: string;
  isOwn: boolean;
};

export const appsInPreview = (
  plugins:
    | Record<
        string,
        {
          name?: string;
          preview?: { owner_id: string; is_own: boolean } | null;
        }
      >
    | undefined
): AppInPreview[] =>
  Object.entries(plugins ?? {})
    .filter(([, plugin]) => plugin?.preview)
    .map(([apiName, plugin]) => ({
      apiName,
      name: plugin.name ?? apiName,
      ownerId: plugin.preview!.owner_id,
      isOwn: plugin.preview!.is_own,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

export type AppNotice = { subpath: string; message: string };

export const appNotices = (
  t: TFunction,
  errorCount = 0,
  appSettingsCount = 0,
  userSettingsCount = 0
): AppNotice[] =>
  [
    errorCount && {
      subpath: '/auth',
      message: t('This app has encountered {{errorCount}} {{noun}}.', {
        errorCount,
        noun: errorCount === 1 ? t('error') : t('errors'),
      }),
    },
    appSettingsCount && {
      subpath: '/configure',
      message:
        appSettingsCount === 1
          ? t(
              '1 required app setting needs to be filled out for this app to work correctly.'
            )
          : t(
              '{{count}} required app settings need to be filled out for this app to work correctly.',
              { count: appSettingsCount }
            ),
    },
    userSettingsCount && {
      subpath: '/user-settings',
      message:
        userSettingsCount === 1
          ? t(
              '1 required user setting needs to be filled out for this app to work correctly.'
            )
          : t(
              '{{count}} required user settings need to be filled out for this app to work correctly.',
              { count: userSettingsCount }
            ),
    },
  ].filter(Boolean) as AppNotice[];

export const previewEndedMessage = (
  t: TFunction,
  {
    reason,
    app_name,
    preview_owner,
    now_using,
    live_version,
  }: PreviewEndedContext,
  currentUserId?: string
) => {
  const isOwn = preview_owner.id === currentUserId;
  const values = { name: preview_owner.full_name, app: app_name };
  const ended = {
    published: isOwn
      ? t('Your preview of {{app}} was published.', values)
      : t("{{name}}'s preview of {{app}} was published.", values),
    deleted: isOwn
      ? t('Your preview of {{app}} was deleted.', values)
      : t("{{name}}'s preview of {{app}} was deleted.", values),
  }[reason];
  const nowUsing = {
    own_preview: t("You're now using your preview."),
    live: t("You're now using published version {{version}}.", {
      version: live_version,
    }),
    none: t('{{app}} is no longer available to you.', values),
  }[now_using];
  return `${ended} ${nowUsing}`;
};
